// Recent Installs — real installation gallery.
import { Link } from "react-router-dom";
import {
  ArrowRight, Camera, Home as HomeIcon, MapPin, Wrench,
  Sparkles, ShieldCheck, Calculator as CalcIcon, PhoneCall, CheckCircle2,
} from "lucide-react";
import Reveal from "@/components/layout/Reveal";

const WHAT_WELL_SHOW = [
  { icon: Wrench,      t: "System replaced",        b: "What the previous setup was — gas, electric, LPG or legacy." },
  { icon: Sparkles,    t: "Product installed",      b: "The AeroMax system chosen for the home, and why it suited." },
  { icon: HomeIcon,    t: "Home type",              b: "From compact townhouses to larger family homes — every layout's different." },
  { icon: MapPin,      t: "Region",                 b: "Where the install took place across New Zealand." },
  { icon: ShieldCheck, t: "Site considerations",    b: "Anything we worked around — access, electrical, plumbing, placement." },
  { icon: Camera,      t: "Customer outcome",       b: "What changed for the household after switching to a heat pump system." },
];

const GALLERY = [
  {
    tag: "Completed install",
    title: "NeoPower Black Shield install",
    subtitle: "Outdoor heat pump hot water installation",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/bo1ehw45_7BF4686B-C5BB-42A4-9F4D-002C7BF9836B.jpg",
    alt: "AeroMax NeoPower Black Shield hot water heat pump installed on the exterior brick wall of a New Zealand home",
  },
  {
    tag: "Completed install",
    title: "Electric cylinder replacement",
    subtitle: "Modern heat pump upgrade for a Kiwi home",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/zbnel7cs_WhatsApp%20Image%202026-07-23%20at%2007.37.13.jpeg",
    alt: "AeroMax NeoPower heat pump replacing an electric cylinder — professionally installed alongside a stone-paved garden path",
  },
  {
    tag: "Completed install",
    title: "Family home upgrade",
    subtitle: "Efficient hot water for everyday household use",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/6h3swu1k_Remove%20Person%20From%20Photo.webp",
    alt: "AeroMax NeoPower heat pump installed next to a gas hot water unit on the exterior wall of a family home",
  },
];

function Hero() {
  return (
    <section data-testid="recent-hero" className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Camera className="h-3 w-3 text-[#10B981]" /> Recent Installs
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Real AeroMax Energy <span className="text-[#10B981]">installs.</span>
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
            A growing gallery of NeoPower Black Shield hot water heat pump installations completed through the AeroMax Energy network across New Zealand.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/free-home-assessment" data-testid="recent-cta-assess" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30">
              Book Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/savings-calculator" data-testid="recent-cta-calc" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all">
              Calculate Your Savings
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function GalleryCard({ tag, title, subtitle, image, alt, i }) {
  return (
    <Reveal delay={i * 70}>
      <div
        data-testid={`recent-card-${i}`}
        className="group rounded-2xl overflow-hidden border border-[#E2E8F0] bg-white shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 hover:border-[#10B981]/30 hover:-translate-y-1 transition-all"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#F1F5F9]">
          <img
            src={image}
            alt={alt}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.03]"
          />
          {/* Bottom gradient for caption readability */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
          {/* Top-left tag */}
          <span className="absolute top-3.5 left-3.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#0B132B] text-[10px] font-bold tracking-[0.16em] uppercase shadow-sm">
            <CheckCircle2 className="h-3 w-3 text-[#10B981]" strokeWidth={2.4} /> {tag}
          </span>
        </div>
        <div className="p-5">
          <h3 className="font-heading text-base font-bold text-[#0B132B] leading-snug">{title}</h3>
          <p className="mt-1.5 text-xs text-[#475569] leading-relaxed">{subtitle}</p>
        </div>
      </div>
    </Reveal>
  );
}

export default function RecentInstalls() {
  return (
    <div className="bg-white">
      <Hero />

      {/* What our install stories will show */}
      <section data-testid="recent-show" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">What our install stories will show</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Every install, <span className="text-[#10B981]">documented transparently.</span>
            </h2>
            <p className="mt-5 text-lg text-[#475569] leading-relaxed">
              When our gallery launches, each case study will share the home, the upgrade and the outcome — no spin, just what actually happened.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WELL_SHOW.map((c, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="h-full rounded-2xl bg-white border border-[#E2E8F0] p-7 hover:border-[#10B981]/40 hover:shadow-lg hover:shadow-emerald-500/8 hover:-translate-y-0.5 transition-all">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/20">
                    <c.icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <h3 className="font-heading mt-5 text-lg font-bold text-[#0B132B] leading-snug">{c.t}</h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">{c.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Installation gallery — real installs */}
      <section data-testid="recent-gallery" className="py-20 sm:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Installation gallery</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Real <span className="text-[#10B981]">AeroMax Energy installs.</span>
            </h2>
            <p className="mt-5 text-lg text-[#475569] leading-relaxed">
              A growing gallery of NeoPower Black Shield hot water heat pump installations completed through the AeroMax Energy network.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {GALLERY.map((g, i) => (
              <GalleryCard key={i} i={i} {...g} />
            ))}
          </div>
        </div>
      </section>

      {/* Want your home assessed? */}
      <section data-testid="recent-final-cta" className="relative py-20 sm:py-24 bg-[#0B132B] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(50%_70%_at_50%_50%,rgba(16,185,129,0.18),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-[1.05]">
              Want your home <span className="text-[#10B981]">assessed?</span>
            </h2>
            <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-2xl mx-auto">
              Your home could be next on our install gallery. Start with a free, no-obligation assessment.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link to="/free-home-assessment" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30">
                <PhoneCall className="h-4 w-4" /> Book Free Assessment
              </Link>
              <Link to="/savings-calculator" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all">
                <CalcIcon className="h-4 w-4" /> Calculate Your Savings
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
